<template>
  <div class="event-layout">
    <EventSidebar :event-id="eventId" />

    <main class="event-main">
      <div class="page-header">
        <div>
          <p class="breadcrumb">Events → {{ eventName }}</p>
          <h1 class="page-title">View Settings</h1>
          <p class="page-subtitle">Configure queue, presenter, and lower thirds display settings per event.</p>
        </div>
        <div class="header-actions">
          <a v-if="canSeePresenter" :href="presenterHref" class="ghost-link" target="_blank" rel="noreferrer">Open Presenter</a>
          <a :href="lowerThirdsHref" class="ghost-link" target="_blank" rel="noreferrer">Open Lower Thirds</a>
        </div>
      </div>

      <div v-if="error" class="notice error">{{ error }}</div>
      <div v-if="successMessage" class="notice success">{{ successMessage }}</div>

      <section class="panel mb-6">
        <div class="panel-header">
          <div>
            <h2 class="panel-title">Queue</h2>
            <p class="panel-copy">Show an imported seat or card reference on student rows in the live queue.</p>
          </div>
        </div>

        <div class="config-grid">
          <section class="config-card">
            <label class="toggle-row">
              <input v-model="viewForm.queue.seatNumberPill.enabled" type="checkbox" />
              <span>Show seat-number pill</span>
            </label>

            <div class="grid gap-4 md:grid-cols-2 mt-4">
              <div>
                <label class="field-label">Imported Field</label>
                <select
                  v-model="viewForm.queue.seatNumberPill.field"
                  class="field-input"
                  :disabled="!viewForm.queue.seatNumberPill.enabled"
                >
                  <option value="">Choose a field</option>
                  <option
                    v-for="option in queueFieldOptions"
                    :key="`queue-seat-${option.value}`"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>
              <div>
                <label class="field-label">Pill Label</label>
                <input
                  v-model.trim="viewForm.queue.seatNumberPill.label"
                  type="text"
                  class="field-input"
                  maxlength="24"
                  placeholder="Seat"
                  :disabled="!viewForm.queue.seatNumberPill.enabled"
                />
              </div>
            </div>

            <div v-if="viewForm.queue.seatNumberPill.enabled" class="queue-pill-preview-row">
              <span v-if="queueSeatPillPreview" class="queue-pill-preview">{{ queueSeatPillPreview }}</span>
              <span v-else class="panel-copy">Choose a populated imported field to show the pill.</span>
            </div>
          </section>
        </div>

        <div class="save-row">
          <button
            class="primary-button"
            @click="saveViews"
            :disabled="saving || !canEditViews || !queueSeatPillIsValid"
          >
            {{ saving ? "Saving..." : "Save View Settings" }}
          </button>
        </div>
      </section>

      <section class="panel mb-6">
        <div class="panel-header">
          <div>
            <h2 class="panel-title">Presenter View</h2>
            <p class="panel-copy">Configure how student, presenter-note, and display-line rows appear to the presenter.</p>
          </div>
          <div class="preset-control">
            <label class="field-label">Preset</label>
            <select v-model="viewForm.presenter.preset" class="field-input" @change="applyPresenterPreset">
              <option v-for="preset in presenterPresets" :key="preset" :value="preset">{{ preset }}</option>
            </select>
          </div>
        </div>

        <div class="config-grid">
          <section class="config-card">
            <h3 class="config-title">Student</h3>
            <div class="grid gap-4 md:grid-cols-2">
              <div>
                <label class="field-label">Headline Field</label>
                <select v-model="viewForm.presenter.student.headlineField" class="field-input">
                  <option v-for="option in fieldOptions" :key="`ph-${option.value}`" :value="option.value">{{ option.label }}</option>
                </select>
              </div>
              <div>
                <label class="field-label">Subline Field</label>
                <select v-model="viewForm.presenter.student.sublineField" class="field-input">
                  <option v-for="option in fieldOptions" :key="`ps-${option.value}`" :value="option.value">{{ option.label }}</option>
                </select>
              </div>
              <div>
                <label class="field-label">Alignment</label>
                <select v-model="viewForm.presenter.student.alignment" class="field-input">
                  <option v-for="alignment in alignments" :key="`pa-${alignment}`" :value="alignment">{{ alignment }}</option>
                </select>
              </div>
              <div>
                <label class="field-label">Font Scale</label>
                <select v-model="viewForm.presenter.student.fontScale" class="field-input">
                  <option v-for="scale in fontScales" :key="`pf-${scale}`" :value="scale">{{ scale }}</option>
                </select>
              </div>
              <div>
                <label class="field-label">Max Width</label>
                <select v-model="viewForm.presenter.student.maxWidth" class="field-input">
                  <option v-for="width in maxWidths" :key="`pw-${width}`" :value="width">{{ width }}</option>
                </select>
              </div>
              <label class="toggle-row">
                <input v-model="viewForm.presenter.student.showTypeLabel" type="checkbox" />
                <span>Show type label</span>
              </label>
            </div>
          </section>

          <section class="config-card">
            <h3 class="config-title">Presenter Note</h3>
            <div class="grid gap-4 md:grid-cols-2">
              <div>
                <label class="field-label">Body Field</label>
                <select v-model="viewForm.presenter.presenterNote.bodyField" class="field-input">
                  <option v-for="option in fieldOptions" :key="`pnb-${option.value}`" :value="option.value">{{ option.label }}</option>
                </select>
              </div>
              <div>
                <label class="field-label">Alignment</label>
                <select v-model="viewForm.presenter.presenterNote.alignment" class="field-input">
                  <option v-for="alignment in alignments" :key="`pna-${alignment}`" :value="alignment">{{ alignment }}</option>
                </select>
              </div>
              <div>
                <label class="field-label">Font Scale</label>
                <select v-model="viewForm.presenter.presenterNote.fontScale" class="field-input">
                  <option v-for="scale in fontScales" :key="`pnf-${scale}`" :value="scale">{{ scale }}</option>
                </select>
              </div>
              <div>
                <label class="field-label">Max Width</label>
                <select v-model="viewForm.presenter.presenterNote.maxWidth" class="field-input">
                  <option v-for="width in maxWidths" :key="`pnw-${width}`" :value="width">{{ width }}</option>
                </select>
              </div>
            </div>
          </section>

          <section class="config-card">
            <h3 class="config-title">Display Line</h3>
            <div class="grid gap-4 md:grid-cols-2">
              <div>
                <label class="field-label">Body Field</label>
                <select v-model="viewForm.presenter.displayLine.bodyField" class="field-input">
                  <option v-for="option in fieldOptions" :key="`pdb-${option.value}`" :value="option.value">{{ option.label }}</option>
                </select>
              </div>
              <div>
                <label class="field-label">Alignment</label>
                <select v-model="viewForm.presenter.displayLine.alignment" class="field-input">
                  <option v-for="alignment in alignments" :key="`pda-${alignment}`" :value="alignment">{{ alignment }}</option>
                </select>
              </div>
              <div>
                <label class="field-label">Font Scale</label>
                <select v-model="viewForm.presenter.displayLine.fontScale" class="field-input">
                  <option v-for="scale in fontScales" :key="`pdf-${scale}`" :value="scale">{{ scale }}</option>
                </select>
              </div>
              <div>
                <label class="field-label">Max Width</label>
                <select v-model="viewForm.presenter.displayLine.maxWidth" class="field-input">
                  <option v-for="width in maxWidths" :key="`pdw-${width}`" :value="width">{{ width }}</option>
                </select>
              </div>
            </div>
          </section>

          <section class="config-card">
            <h3 class="config-title">Lower Thirds Branding</h3>
            <div class="grid gap-4 md:grid-cols-2">
              <div>
                <label class="field-label">Logo</label>
                <select v-model="viewForm.lowerThirds.branding.logoPreset" class="field-input">
                  <option v-for="preset in logoPresets" :key="preset" :value="preset">{{ preset }}</option>
                </select>
              </div>
              <div v-if="viewForm.lowerThirds.branding.logoPreset === 'custom'">
                <label class="field-label">Logo URL</label>
                <input
                  v-model.trim="viewForm.lowerThirds.branding.logoUrl"
                  type="url"
                  class="field-input"
                  placeholder="https://example.com/logo.svg"
                />
              </div>
              <div v-if="viewForm.lowerThirds.branding.logoPreset !== 'none'">
                <label class="field-label">Logo Size</label>
                <select v-model="viewForm.lowerThirds.branding.logoSize" class="field-input">
                  <option v-for="size in logoSizes" :key="size" :value="size">{{ logoSizeLabel(size) }}</option>
                </select>
              </div>
              <div>
                <label class="field-label">Background</label>
                <input v-model="viewForm.lowerThirds.branding.backgroundColor" type="color" class="field-input color-input" />
              </div>
              <div>
                <label class="field-label">Text</label>
                <input v-model="viewForm.lowerThirds.branding.textColor" type="color" class="field-input color-input" />
              </div>
              <div class="full-span">
                <label class="field-label">Font Family</label>
                <input
                  v-model.trim="viewForm.lowerThirds.branding.fontFamily"
                  type="text"
                  class="field-input"
                  placeholder="Arial, sans-serif"
                />
              </div>
            </div>
          </section>
        </div>

        <div class="preview-section">
          <h3 class="preview-title">Presenter Preview</h3>
          <div class="preview-grid">
            <section class="preview-card">
              <p class="preview-label">Student</p>
              <div class="presenter-preview-stage">
                <div class="presenter-preview-content" :style="presenterShellStyle(viewForm.presenter.student)">
                  <p
                    v-if="viewForm.presenter.student.showTypeLabel"
                    class="preview-type"
                    :style="{ textAlign: viewForm.presenter.student.alignment }"
                  >
                    student
                  </p>
                  <div class="preview-student-headline" :style="presenterHeadlineStyle(viewForm.presenter.student)">
                    {{ presenterPreviewValue(studentPreviewItem, viewForm.presenter.student.headlineField) }}
                  </div>
                  <div class="preview-student-subline" :style="presenterSublineStyle(viewForm.presenter.student)">
                    {{ presenterPreviewValue(studentPreviewItem, viewForm.presenter.student.sublineField) }}
                  </div>
                </div>
              </div>
            </section>

            <section class="preview-card">
              <p class="preview-label">Presenter Note</p>
              <div class="presenter-preview-stage">
                <div class="presenter-preview-content" :style="presenterShellStyle(viewForm.presenter.presenterNote)">
                  <div class="preview-body" :style="presenterBodyStyle(viewForm.presenter.presenterNote)">
                    {{ presenterPreviewValue(presenterNotePreviewItem, viewForm.presenter.presenterNote.bodyField) }}
                  </div>
                </div>
              </div>
            </section>

            <section class="preview-card">
              <p class="preview-label">Display Line</p>
              <div class="presenter-preview-stage">
                <div class="presenter-preview-content" :style="presenterShellStyle(viewForm.presenter.displayLine)">
                  <div class="preview-body" :style="presenterBodyStyle(viewForm.presenter.displayLine)">
                    {{ presenterPreviewValue(displayLinePreviewItem, viewForm.presenter.displayLine.bodyField) }}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </section>

      <section class="panel">
        <div class="panel-header">
          <div>
            <h2 class="panel-title">Lower Thirds Output</h2>
            <p class="panel-copy">Configure which fields the lower third and headline views use for this event.</p>
          </div>
          <div class="preset-control">
            <label class="field-label">Preset</label>
            <select v-model="viewForm.lowerThirds.preset" class="field-input" @change="applyLowerThirdsPreset">
              <option v-for="preset in lowerThirdsPresets" :key="preset" :value="preset">{{ preset }}</option>
            </select>
          </div>
        </div>

        <div class="config-grid">
          <section class="config-card">
            <h3 class="config-title">Student</h3>
            <div class="grid gap-4 md:grid-cols-2">
              <div>
                <label class="field-label">Primary Field</label>
                <select v-model="viewForm.lowerThirds.student.primaryField" class="field-input">
                  <option v-for="option in fieldOptions" :key="`vh-${option.value}`" :value="option.value">{{ option.label }}</option>
                </select>
              </div>
              <div>
                <label class="field-label">Secondary Field</label>
                <select v-model="viewForm.lowerThirds.student.secondaryField" class="field-input">
                  <option v-for="option in fieldOptions" :key="`vs-${option.value}`" :value="option.value">{{ option.label }}</option>
                </select>
              </div>
              <div>
                <label class="field-label">Alignment</label>
                <select v-model="viewForm.lowerThirds.student.alignment" class="field-input">
                  <option v-for="alignment in alignments" :key="`va-${alignment}`" :value="alignment">{{ alignment }}</option>
                </select>
              </div>
              <div>
                <label class="field-label">Font Scale</label>
                <select v-model="viewForm.lowerThirds.student.fontScale" class="field-input">
                  <option v-for="scale in fontScales" :key="`vf-${scale}`" :value="scale">{{ scale }}</option>
                </select>
              </div>
              <label class="toggle-row">
                <input v-model="viewForm.lowerThirds.student.showSecondaryLine" type="checkbox" />
                <span>Show secondary line</span>
              </label>
            </div>
          </section>

          <section class="config-card">
            <h3 class="config-title">Display Line</h3>
            <div class="grid gap-4 md:grid-cols-2">
              <div>
                <label class="field-label">Headline Field</label>
                <select v-model="viewForm.lowerThirds.displayLine.headlineField" class="field-input">
                  <option v-for="option in fieldOptions" :key="`vdb-${option.value}`" :value="option.value">{{ option.label }}</option>
                </select>
              </div>
              <div>
                <label class="field-label">Alignment</label>
                <select v-model="viewForm.lowerThirds.displayLine.alignment" class="field-input">
                  <option v-for="alignment in alignments" :key="`vda-${alignment}`" :value="alignment">{{ alignment }}</option>
                </select>
              </div>
              <div>
                <label class="field-label">Font Scale</label>
                <select v-model="viewForm.lowerThirds.displayLine.fontScale" class="field-input">
                  <option v-for="scale in fontScales" :key="`vdf-${scale}`" :value="scale">{{ scale }}</option>
                </select>
              </div>
            </div>
          </section>
        </div>

        <div class="preview-section">
          <h3 class="preview-title">Lower Thirds Preview</h3>
          <div class="preview-grid">
            <section class="preview-card">
              <p class="preview-label">Student</p>
              <div class="lower-thirds-preview-stage">
                <div
                  class="lower-thirds-preview-bar student"
                  :class="{ 'has-logo': lowerThirdsLogoUrl }"
                  :style="lowerThirdsStudentBoxStyle"
                >
                  <div
                    v-if="lowerThirdsLogoUrl"
                    class="lower-thirds-preview-logo-frame"
                    :style="lowerThirdsLogoStyle"
                  >
                    <img class="lower-thirds-preview-logo" :src="lowerThirdsLogoUrl" alt="Event logo" />
                  </div>
                  <div class="lower-thirds-preview-copy">
                    <div class="lower-thirds-primary-line" :style="lowerThirdsStudentPrimaryStyle">
                      {{ presenterPreviewValue(studentPreviewItem, viewForm.lowerThirds.student.primaryField) }}
                    </div>
                    <div
                      v-if="viewForm.lowerThirds.student.showSecondaryLine"
                      class="lower-thirds-secondary-line"
                      :style="lowerThirdsStudentSecondaryStyle"
                    >
                      {{ presenterPreviewValue(studentPreviewItem, viewForm.lowerThirds.student.secondaryField) }}
                    </div>
                  </div>
                  <div v-if="lowerThirdsLogoUrl" class="lower-thirds-preview-spacer"></div>
                </div>
              </div>
            </section>

            <section class="preview-card">
              <p class="preview-label">Display Line</p>
              <div class="lower-thirds-preview-stage">
                <div class="lower-thirds-preview-bar display-line" :style="lowerThirdsDisplayBoxStyle">
                  <div class="lower-thirds-headline-only" :style="lowerThirdsDisplayHeadlineStyle">
                    {{ presenterPreviewValue(displayLinePreviewItem, viewForm.lowerThirds.displayLine.headlineField) }}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        <div class="save-row">
          <button
            class="primary-button"
            @click="saveViews"
            :disabled="saving || !canEditViews || !queueSeatPillIsValid"
          >
            {{ saving ? "Saving..." : "Save View Settings" }}
          </button>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { fetchEvent, updateViews } from "@/lib/api";
import { hasAnyRole } from "@/lib/auth";
import EventSidebar from "./EventSidebar.vue";
import {
  ALIGNMENT_OPTIONS,
  buildFieldOptions,
  buildQueueFieldOptions,
  cloneLowerThirdsPreset,
  clonePresenterPreset,
  cloneViews,
  DEFAULT_VIEWS,
  FONT_SCALE_OPTIONS,
  LOWER_THIRDS_LOGO_SIZE_OPTIONS,
  LOWER_THIRDS_PRESET_OPTIONS,
  LOWER_THIRDS_LOGO_PRESET_OPTIONS,
  MAX_WIDTH_OPTIONS,
  PRESENTER_PRESET_OPTIONS,
  lowerThirdsBrandingStyle,
  lowerThirdsFontStyles,
  lowerThirdsLogoStyle,
  presenterFontStyles,
  presenterWidthValue,
  resolveLowerThirdsLogoUrl,
  resolveDisplayValue,
  suggestSeatNumberField,
} from "@/lib/viewConfig";

export default {
  name: "EventViewsView",
  components: {
    EventSidebar,
  },
  data() {
    return {
      snapshot: null,
      viewForm: cloneViews(DEFAULT_VIEWS),
      fieldOptions: buildFieldOptions(null),
      queueFieldOptions: buildQueueFieldOptions(null),
      saving: false,
      error: "",
      successMessage: "",
      alignments: ALIGNMENT_OPTIONS,
      fontScales: FONT_SCALE_OPTIONS,
      maxWidths: MAX_WIDTH_OPTIONS,
      presenterPresets: PRESENTER_PRESET_OPTIONS,
      lowerThirdsPresets: LOWER_THIRDS_PRESET_OPTIONS,
      logoPresets: LOWER_THIRDS_LOGO_PRESET_OPTIONS,
      logoSizes: LOWER_THIRDS_LOGO_SIZE_OPTIONS,
    };
  },
  computed: {
    eventId() {
      return this.$route.params.eventId;
    },
    eventName() {
      return this.snapshot?.event?.name || this.eventId;
    },
    presenterHref() {
      return this.$router.resolve(`/events/${encodeURIComponent(this.eventId)}/presenter`).href;
    },
    lowerThirdsHref() {
      return this.$router.resolve(`/events/${encodeURIComponent(this.eventId)}/lower-thirds`).href;
    },
    lowerThirdsLogoUrl() {
      return resolveLowerThirdsLogoUrl(this.viewForm.lowerThirds.branding);
    },
    lowerThirdsBrandingStyle() {
      return lowerThirdsBrandingStyle(this.viewForm.lowerThirds.branding);
    },
    lowerThirdsLogoStyle() {
      return lowerThirdsLogoStyle(this.viewForm.lowerThirds.branding);
    },
    canEditViews() {
      return hasAnyRole(["internal_admin", "uni_admin"]);
    },
    canSeePresenter() {
      return hasAnyRole(["internal_admin", "uni_admin", "operator", "orator"]);
    },
    queueSeatPillPreview() {
      const pill = this.viewForm.queue.seatNumberPill;
      if (!pill.enabled || !pill.field) {
        return "";
      }
      const items = [
        ...(this.snapshot?.queue?.items || []),
        ...(this.snapshot?.participants?.items || []),
      ];
      const previewItem = items.find(
        (item) => item.type === "student" && `${resolveDisplayValue(item, pill.field) || ""}`.trim()
      );
      const value = `${resolveDisplayValue(previewItem, pill.field) || ""}`.trim();
      if (!value) {
        return "";
      }
      return [pill.label.trim(), value].filter(Boolean).join(" ");
    },
    queueSeatPillIsValid() {
      const pill = this.viewForm.queue.seatNumberPill;
      return !pill.enabled || this.queueFieldOptions.some((option) => option.value === pill.field);
    },
    studentPreviewItem() {
      return this.findPreviewItem("student", {
        fullName: "Amina Khan",
        award: "Master of Arts",
        text: "",
        extraFields: {
          faculty: "Faculty of Arts",
          award_short: "MA",
        },
      });
    },
    presenterNotePreviewItem() {
      return this.findPreviewItem("presenter_note", {
        text: "Now to present the degrees for the Faculty of Arts",
        extraFields: {
          faculty: "Faculty of Arts",
        },
      });
    },
    displayLinePreviewItem() {
      return this.findPreviewItem("display_line", {
        text: "Master of Arts",
        extraFields: {
          award_short: "MA",
        },
      });
    },
    lowerThirdsStudentBoxStyle() {
      return {
        ...this.lowerThirdsBrandingStyle,
        textAlign: this.viewForm.lowerThirds.student.alignment,
      };
    },
    lowerThirdsStudentPrimaryStyle() {
      const fonts = lowerThirdsFontStyles(this.viewForm.lowerThirds.student.fontScale);
      return {
        fontSize: fonts.primary,
        textAlign: this.viewForm.lowerThirds.student.alignment,
      };
    },
    lowerThirdsStudentSecondaryStyle() {
      const fonts = lowerThirdsFontStyles(this.viewForm.lowerThirds.student.fontScale);
      return {
        fontSize: fonts.secondary,
        textAlign: this.viewForm.lowerThirds.student.alignment,
      };
    },
    lowerThirdsDisplayBoxStyle() {
      return {
        ...this.lowerThirdsBrandingStyle,
        alignItems: this.viewForm.lowerThirds.displayLine.alignment === "center" ? "center" : "flex-start",
      };
    },
    lowerThirdsDisplayHeadlineStyle() {
      const fonts = lowerThirdsFontStyles(this.viewForm.lowerThirds.displayLine.fontScale);
      return {
        fontSize: fonts.headline,
        textAlign: this.viewForm.lowerThirds.displayLine.alignment,
      };
    },
  },
  methods: {
    findPreviewItem(type, fallback) {
      const queueItems = this.snapshot?.queue?.items || [];
      const participantItems = this.snapshot?.participants?.items || [];
      return (
        queueItems.find((item) => item.type === type) ||
        participantItems.find((item) => item.type === type) || {
          type,
          ...fallback,
        }
      );
    },
    presenterPreviewValue(item, fieldName) {
      return resolveDisplayValue(item, fieldName);
    },
    presenterShellStyle(config) {
      return {
        textAlign: config.alignment,
        maxWidth: presenterWidthValue(config.maxWidth || "wide"),
        marginLeft: config.alignment === "center" ? "auto" : "0",
        marginRight: config.alignment === "center" ? "auto" : "0",
      };
    },
    presenterHeadlineStyle(config) {
      const fonts = presenterFontStyles(config.fontScale);
      return {
        fontSize: fonts.headline,
        textAlign: config.alignment,
      };
    },
    presenterSublineStyle(config) {
      const fonts = presenterFontStyles(config.fontScale);
      return {
        fontSize: fonts.subline,
        textAlign: config.alignment,
      };
    },
    presenterBodyStyle(config) {
      const fonts = presenterFontStyles(config.fontScale);
      return {
        fontSize: fonts.body,
        textAlign: config.alignment,
      };
    },
    applyPresenterPreset() {
      this.viewForm.presenter = clonePresenterPreset(this.viewForm.presenter.preset);
    },
    applyLowerThirdsPreset() {
      const branding = { ...(this.viewForm.lowerThirds.branding || {}) };
      this.viewForm.lowerThirds = {
        ...cloneLowerThirdsPreset(this.viewForm.lowerThirds.preset),
        branding,
      };
    },
    logoSizeLabel(size) {
      return {
        sm: "Small",
        md: "Medium",
        lg: "Large",
      }[size] || size;
    },
    async loadEvent() {
      this.error = "";
      this.successMessage = "";
      try {
        const snapshot = await fetchEvent(this.eventId);
        this.snapshot = snapshot;
        this.viewForm = cloneViews(snapshot.views || DEFAULT_VIEWS, snapshot.event);
        this.fieldOptions = buildFieldOptions(snapshot);
        this.queueFieldOptions = buildQueueFieldOptions(snapshot);
        if (!this.viewForm.queue.seatNumberPill.field) {
          this.viewForm.queue.seatNumberPill.field = suggestSeatNumberField(this.queueFieldOptions);
        }
      } catch (error) {
        this.error = "Unable to load event view settings.";
      }
    },
    async saveViews() {
      if (!this.canEditViews || !this.queueSeatPillIsValid) {
        return;
      }
      this.saving = true;
      this.error = "";
      this.successMessage = "";
      try {
        const snapshot = await updateViews(this.eventId, this.viewForm);
        this.snapshot = snapshot;
        this.viewForm = cloneViews(snapshot.views || DEFAULT_VIEWS, snapshot.event);
        this.fieldOptions = buildFieldOptions(snapshot);
        this.queueFieldOptions = buildQueueFieldOptions(snapshot);
        this.successMessage = "View settings saved. Refresh any open Queue, Presenter, or Lower Thirds views.";
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to save view settings.";
      } finally {
        this.saving = false;
      }
    },
  },
  mounted() {
    this.loadEvent();
  },
};
</script>

<style scoped>
.event-layout {
  display: flex;
  gap: 1rem;
  width: calc(100vw - 0.25rem);
  max-width: none;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 0 0.125rem 2.5rem;
}

.event-main {
  flex: 1 1 auto;
  min-width: 0;
}

:deep(.sidebar) {
  width: 168px;
  flex-basis: 168px;
}

.page-header,
.header-actions,
.panel-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  flex-wrap: wrap;
}

.breadcrumb {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--orator-graphite);
}

.page-title {
  margin-top: 0.35rem;
  font-size: 2rem;
  font-weight: 900;
  color: var(--orator-navy);
}

.page-subtitle,
.panel-copy {
  margin-top: 0.35rem;
  color: var(--orator-graphite);
  font-weight: 500;
}

.panel {
  background: white;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.06);
  padding: 1.5rem;
}

.panel-title {
  font-size: 1.5rem;
  font-weight: 900;
  color: var(--orator-navy);
}

.preset-control {
  min-width: 14rem;
}

.config-grid {
  margin-top: 1.25rem;
  display: grid;
  gap: 1rem;
}

.config-card {
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  padding: 1rem;
}

.config-title {
  font-size: 1.1rem;
  font-weight: 800;
  color: var(--orator-navy);
  margin-bottom: 1rem;
}

.field-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--orator-arsenic);
  margin-bottom: 0.5rem;
}

.field-input {
  display: block;
  width: 100%;
  border-radius: 0.45rem;
  border: 1px solid var(--orator-steel);
  padding: 0.625rem 0.75rem;
  background: white;
}

.color-input {
  min-height: 2.75rem;
  padding: 0.25rem 0.35rem;
}

.full-span {
  grid-column: 1 / -1;
}

.toggle-row {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-weight: 600;
  color: var(--orator-arsenic);
}

.queue-pill-preview-row {
  min-height: 2.25rem;
  margin-top: 1rem;
  display: flex;
  align-items: center;
}

.queue-pill-preview {
  display: inline-flex;
  align-items: center;
  border: 1px solid #d6ad00;
  border-radius: 999px;
  padding: 0.25rem 0.7rem;
  background: #fff7cc;
  color: #473800;
  font-size: 0.82rem;
  font-weight: 900;
  white-space: nowrap;
}

.save-row {
  margin-top: 1.5rem;
}

.preview-section {
  margin-top: 1.5rem;
}

.preview-title {
  font-size: 1.1rem;
  font-weight: 800;
  color: var(--orator-navy);
  margin-bottom: 0.75rem;
}

.preview-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

.preview-card {
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  padding: 0.9rem;
  background: #fafafa;
}

.preview-label {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--orator-graphite);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 0.75rem;
}

.presenter-preview-stage {
  background: white;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  min-height: 16rem;
  padding: 1.25rem;
}

.presenter-preview-content {
  width: 100%;
}

.preview-type {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--orator-graphite);
  margin-bottom: 0.75rem;
}

.preview-student-headline,
.preview-student-subline,
.preview-body {
  line-height: 1.15;
  color: var(--orator-navy);
}

.preview-student-headline {
  font-weight: 900;
}

.preview-student-subline {
  margin-top: 0.9rem;
  color: var(--orator-graphite);
}

.preview-body {
  font-weight: 700;
}

.lower-thirds-preview-stage {
  position: relative;
  min-height: 15rem;
  border-radius: 0.75rem;
  background: #00fd19;
  overflow: hidden;
  font-family: Arial, sans-serif;
}

.lower-thirds-preview-bar {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  min-height: 24%;
  padding: 0 2.5rem;
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
}

.lower-thirds-preview-bar.has-logo {
  grid-template-columns: auto 1fr auto;
}

.lower-thirds-preview-bar.student {
  background: #2f2552;
  color: white;
}

.lower-thirds-preview-bar.display-line {
  background: #2f2552;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lower-thirds-primary-line,
.lower-thirds-secondary-line,
.lower-thirds-headline-only {
  line-height: 1.1;
  margin: 0;
}

.lower-thirds-primary-line,
.lower-thirds-headline-only {
  font-weight: 400;
}

.lower-thirds-secondary-line {
  margin-top: 0.45rem;
}

.lower-thirds-preview-logo-frame {
  width: 100%;
  height: 72%;
  max-height: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.lower-thirds-preview-logo {
  width: auto;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.lower-thirds-preview-copy {
  grid-column: 1;
  text-align: center;
  color: inherit;
}

.lower-thirds-preview-bar.has-logo .lower-thirds-preview-copy {
  grid-column: 2;
}

.lower-thirds-preview-spacer {
  width: 1px;
  height: 1px;
}

.primary-button,
.ghost-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem 0.95rem;
  border-radius: 0.45rem;
  font-weight: 700;
  text-decoration: none;
  border: 1px solid var(--orator-steel);
}

.primary-button {
  background: var(--orator-blue);
  border-color: var(--orator-blue);
  color: white;
}

.ghost-link {
  background: white;
  color: var(--orator-arsenic);
}

.notice {
  margin-bottom: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 0.65rem;
  font-weight: 600;
}

.notice.error {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

.notice.success {
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
}

@media (max-width: 960px) {
  .event-layout {
    width: auto;
    flex-direction: column;
    padding: 0 1rem 2.5rem;
  }

  :deep(.sidebar) {
    width: 100%;
    flex-basis: 100%;
  }
}
</style>
